import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLearningPath } from '../services/firebase';
import {
    loadDeckCards,
    getDeckStats,
    getDueCards,
    loadMasterDeck,
    getAllDecksForPath
} from '../services/spacedRepetition';
import './FlashcardDecks.css';

function FlashcardDecks() {
    const [pathData, setPathData] = useState(null);
    const [decks, setDecks] = useState([]);
    const [masterDeck, setMasterDeck] = useState(null);
    const [loading, setLoading] = useState(true);
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { topic } = location.state || {};

    useEffect(() => {
        if (!topic) {
            navigate('/flashcards');
            return;
        }
        loadPathAndDecks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic]);

    const loadPathAndDecks = async () => {
        setLoading(true);
        try {
            // Load the full learning path
            const result = await getLearningPath(currentUser.uid, topic);

            if (!result.success || !result.data) {
                navigate('/flashcards');
                return;
            }

            setPathData(result.data);

            // Get all modules (levels)
            const modules = result.data.modules || [];

            // Load deck data for each level
            const deckData = modules.map((module, index) => {
                const levelNum = index + 1;
                const cards = loadDeckCards(currentUser.uid, topic, levelNum);
                const stats = cards ? getDeckStats(cards) : { total: 0, due: 0, new: 0, learning: 0, mature: 0 };
                const dueCards = cards ? getDueCards(cards) : [];

                // Determine if this level is unlocked (previous levels have cards or it's level 1)
                const isUnlocked = levelNum === 1 ||
                    (modules.slice(0, index).some((_, i) => {
                        const prevCards = loadDeckCards(currentUser.uid, topic, i + 1);
                        return prevCards && prevCards.length > 0;
                    }));

                // Determine completion status based on lesson progress
                const lessonProgress = result.data.progress?.modules?.[module.id];
                const isCompleted = lessonProgress?.completed || false;

                return {
                    levelNum,
                    moduleId: module.id,
                    title: module.title,
                    description: module.description,
                    concepts: module.concepts || [],
                    cards: cards || [],
                    stats,
                    dueCount: dueCards.length,
                    isUnlocked,
                    isCompleted,
                    hasCards: cards && cards.length > 0
                };
            });

            setDecks(deckData);

            // Check for master deck (only if path is complete)
            const pathComplete = result.data.progress?.completed || false;
            if (pathComplete) {
                const allDecks = getAllDecksForPath(currentUser.uid, topic);
                if (allDecks && allDecks.length > 0) {
                    const allCards = allDecks.flat();
                    const masterStats = getDeckStats(allCards);
                    const masterDue = getDueCards(allCards);
                    setMasterDeck({
                        cards: allCards,
                        stats: masterStats,
                        dueCount: masterDue.length
                    });
                }
            }

        } catch (error) {
            console.error('Error loading path and decks:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeckClick = (deck) => {
        if (!deck.hasCards) {
            // No cards yet - show message
            return;
        }

        navigate('/flashcards-study', {
            state: {
                topic,
                level: deck.levelNum,
                moduleId: deck.moduleId,
                moduleTitle: deck.title,
                deckMode: 'level'
            }
        });
    };

    const handleMasterDeckClick = () => {
        if (!masterDeck || masterDeck.cards.length === 0) return;

        navigate('/flashcards-study', {
            state: {
                topic,
                deckMode: 'master'
            }
        });
    };

    const handleStudyAll = () => {
        // Study all due cards across all decks
        const allCards = decks.reduce((acc, deck) => [...acc, ...deck.cards], []);
        if (allCards.length === 0) return;

        navigate('/flashcards-study', {
            state: {
                topic,
                deckMode: 'all'
            }
        });
    };

    const getTotalStats = () => {
        return decks.reduce((acc, deck) => ({
            total: acc.total + deck.stats.total,
            due: acc.due + deck.dueCount,
            new: acc.new + deck.stats.new,
            learning: acc.learning + deck.stats.learning,
            mature: acc.mature + deck.stats.mature
        }), { total: 0, due: 0, new: 0, learning: 0, mature: 0 });
    };

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <p>Loading your decks...</p>
            </div>
        );
    }

    const totalStats = getTotalStats();
    const hasAnyCards = totalStats.total > 0;

    return (
        <div className="flashcard-decks-container">
            <div className="decks-header">
                <button className="btn btn-secondary back-btn" onClick={() => navigate('/flashcards')}>
                    ← Back to Topics
                </button>
                <div className="header-content">
                    <h1>📚 {topic}</h1>
                    <p className="subtitle">Study decks organized by level</p>
                </div>
            </div>

            {/* Overall Stats */}
            {hasAnyCards && (
                <div className="overall-stats">
                    <div className="stat-card">
                        <span className="stat-number">{totalStats.total}</span>
                        <span className="stat-label">Total Cards</span>
                    </div>
                    <div className="stat-card due">
                        <span className="stat-number">{totalStats.due}</span>
                        <span className="stat-label">Due Today</span>
                    </div>
                    <div className="stat-card new">
                        <span className="stat-number">{totalStats.new}</span>
                        <span className="stat-label">New</span>
                    </div>
                    <div className="stat-card learning">
                        <span className="stat-number">{totalStats.learning}</span>
                        <span className="stat-label">Learning</span>
                    </div>
                    <div className="stat-card mature">
                        <span className="stat-number">{totalStats.mature}</span>
                        <span className="stat-label">Mastered</span>
                    </div>

                    {totalStats.due > 0 && (
                        <button className="btn btn-primary study-all-btn" onClick={handleStudyAll}>
                            Study All Due ({totalStats.due})
                        </button>
                    )}
                </div>
            )}

            {/* Master Deck (if path complete) */}
            {masterDeck && (
                <div className="master-deck-section">
                    <div
                        className="master-deck-card"
                        onClick={handleMasterDeckClick}
                    >
                        <div className="master-badge">
                            <span className="crown">👑</span>
                            <span>MASTER DECK</span>
                        </div>
                        <h2>Complete {topic} Deck</h2>
                        <p>All cards from every level combined</p>
                        <div className="master-stats">
                            <span className="stat">{masterDeck.stats.total} cards</span>
                            {masterDeck.dueCount > 0 && (
                                <span className="stat due">{masterDeck.dueCount} due</span>
                            )}
                            <span className="stat mature">{masterDeck.stats.mature} mastered</span>
                        </div>
                        <button className="btn btn-primary">Study Master Deck</button>
                    </div>
                </div>
            )}

            {/* Level Decks */}
            <div className="decks-grid">
                {decks.map((deck) => (
                    <div
                        key={deck.levelNum}
                        className={`deck-card ${deck.hasCards ? 'has-cards' : 'empty'} ${deck.isCompleted ? 'completed' : ''}`}
                        onClick={() => handleDeckClick(deck)}
                    >
                        <div className="deck-header">
                            <div className="level-badge">
                                <span>Level {deck.levelNum}</span>
                                {deck.isCompleted && <span className="check">✓</span>}
                            </div>
                            {deck.dueCount > 0 && (
                                <span className="due-badge">{deck.dueCount} due</span>
                            )}
                        </div>

                        <h3 className="deck-title">{deck.title}</h3>

                        {deck.description && (
                            <p className="deck-description">{deck.description}</p>
                        )}

                        {deck.hasCards ? (
                            <div className="deck-stats">
                                <div className="mini-stats">
                                    <span className="mini-stat">
                                        <span className="value">{deck.stats.total}</span>
                                        <span className="label">Cards</span>
                                    </span>
                                    <span className="mini-stat new">
                                        <span className="value">{deck.stats.new}</span>
                                        <span className="label">New</span>
                                    </span>
                                    <span className="mini-stat learning">
                                        <span className="value">{deck.stats.learning}</span>
                                        <span className="label">Learning</span>
                                    </span>
                                    <span className="mini-stat mature">
                                        <span className="value">{deck.stats.mature}</span>
                                        <span className="label">Mastered</span>
                                    </span>
                                </div>

                                {/* Progress bar */}
                                <div className="mastery-progress">
                                    <div
                                        className="progress-fill"
                                        style={{
                                            width: `${deck.stats.total > 0 ? (deck.stats.mature / deck.stats.total) * 100 : 0}%`
                                        }}
                                    />
                                </div>

                                <button className="btn btn-primary btn-small">
                                    {deck.dueCount > 0 ? `Study (${deck.dueCount} due)` : 'Review Deck'}
                                </button>
                            </div>
                        ) : (
                            <div className="empty-deck-message">
                                <span className="empty-icon">📝</span>
                                <p>Complete this level's lesson to unlock flashcards</p>
                                {deck.concepts && deck.concepts.length > 0 && (
                                    <div className="concepts-preview">
                                        <span className="concepts-label">Topics covered:</span>
                                        <span className="concepts-list">
                                            {deck.concepts.slice(0, 3).join(', ')}
                                            {deck.concepts.length > 3 && ` +${deck.concepts.length - 3} more`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {!hasAnyCards && (
                <div className="no-cards-message">
                    <div className="empty-icon">📇</div>
                    <h2>No Flashcards Yet</h2>
                    <p>Complete lessons in your learning path to automatically generate flashcards for each level!</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate('/learning-path', { state: { topic } })}
                    >
                        Go to Learning Path
                    </button>
                </div>
            )}
        </div>
    );
}

export default FlashcardDecks;
